import { useCallback, useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConnectionError from "../../../components/ConnectionError/ConnectionError";
import EmptyState from "../../../components/EmptyState/EmptyState";
import { Modal } from "../../../components/common/Modal/Modal";
import { ConfirmationModal } from "../../../components/common/ConfirmationModal/ConfirmationModal";
import { Pagination } from "../../../components/common/Pagination/Pagination";
import { useShowScrollTop } from "../../../hooks/useShowScrollTop";
import { adminService } from "../../../services/adminService";
import { getErrorMessage } from "../../../utils/errors";
import type { ConsultationServiceItem, ConsultationServiceRequestData } from "../../../types";
import "./ConsultationServicesPage.css";

interface ConsultationServiceFormState {
  titleEn: string;
  titleAr: string;
  summaryEn: string;
  summaryAr: string;
  durationMinutes: string;
  sortOrder: string;
}

const emptyForm: ConsultationServiceFormState = {
  titleEn: "",
  titleAr: "",
  summaryEn: "",
  summaryAr: "",
  durationMinutes: "",
  sortOrder: "0",
};

function ConsultationServicesPage() {
  const { t } = useTranslation();
  const [services, setServices] = useState<ConsultationServiceItem[]>([]);

  // Pagination & Loading State
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Search State
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Form Modal State (shared for create/edit)
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<ConsultationServiceItem | null>(null);
  const [form, setForm] = useState<ConsultationServiceFormState>(emptyForm);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [saving, setSaving] = useState(false);

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<ConsultationServiceItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Floating Go to Top hook
  const { showScrollTop, scrollToTop } = useShowScrollTop(300);

  // Smart debounce: 700ms — enough time to finish typing.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 700);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load a specific page (replaces list content)
  const loadServices = useCallback(
    async (pageToLoad: number) => {
      setIsFetching(true);
      setError(false);
      try {
        const response = await adminService.getConsultationServices(debouncedSearch, pageToLoad, 10);
        setServices(response.content);
        setPage(pageToLoad);
        setTotalPages(response.totalPages);
      } catch {
        setError(true);
      } finally {
        setIsFetching(false);
        setInitialLoading(false);
      }
    },
    [debouncedSearch]
  );

  // Re-fetch from page 0 whenever the debounced search changes (incl. initial mount)
  useEffect(() => {
    loadServices(0);
  }, [loadServices]);

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearch("");
  };

  // Clean up object URL previews
  useEffect(() => {
    return () => {
      if (imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const openCreateModal = () => {
    setEditingService(null);
    setForm(emptyForm);
    setImageFile(null);
    setImagePreview("");
    setFormModalOpen(true);
  };

  const openEditModal = (service: ConsultationServiceItem) => {
    setEditingService(service);
    setForm({
      titleEn: service.titleEn,
      titleAr: service.titleAr,
      summaryEn: service.summaryEn || "",
      summaryAr: service.summaryAr || "",
      durationMinutes:
        service.durationMinutes !== undefined && service.durationMinutes !== null
          ? String(service.durationMinutes)
          : "",
      sortOrder: String(service.sortOrder),
    });
    setImageFile(null);
    setImagePreview(service.imageUrl || "");
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    if (saving) return;
    setFormModalOpen(false);
    setEditingService(null);
    setImageFile(null);
    setImagePreview("");
  };

  const handleFieldChange =
    (field: keyof ConsultationServiceFormState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((current) => ({ ...current, [field]: event.target.value }));
    };

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setImageFile(file);
    if (imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(file ? URL.createObjectURL(file) : editingService?.imageUrl || "");
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      let imageUrl = editingService?.imageUrl;
      if (imageFile) {
        const uploaded = await adminService.uploadImage(imageFile);
        imageUrl = uploaded.url;
      }

      const data: ConsultationServiceRequestData = {
        titleEn: form.titleEn.trim(),
        titleAr: form.titleAr.trim(),
        sortOrder: Number(form.sortOrder) || 0,
      };
      if (form.summaryEn.trim()) data.summaryEn = form.summaryEn.trim();
      if (form.summaryAr.trim()) data.summaryAr = form.summaryAr.trim();
      if (imageUrl) data.imageUrl = imageUrl;
      if (form.durationMinutes.trim()) {
        data.durationMinutes = Number(form.durationMinutes);
      }

      if (editingService) {
        await adminService.updateConsultationService(editingService.id, data);
        toast.success(t("admin.consultationServiceUpdated"));
      } else {
        await adminService.createConsultationService(data);
        toast.success(t("admin.consultationServiceCreated"));
      }
      setFormModalOpen(false);
      setEditingService(null);
      setImageFile(null);
      setImagePreview("");
      loadServices(page);
    } catch (submitError) {
      toast.error(await getErrorMessage(submitError, t("admin.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (service: ConsultationServiceItem) => {
    setServiceToDelete(service);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!serviceToDelete || deleting) return;
    setDeleting(true);
    try {
      await adminService.deleteConsultationService(serviceToDelete.id);
      toast.success(t("admin.consultationServiceDeleted"));
      setDeleteModalOpen(false);
      setServiceToDelete(null);
      // If we just removed the last card on a page beyond the first, step back one page
      loadServices(services.length === 1 && page > 0 ? page - 1 : page);
    } catch (deleteError) {
      toast.error(await getErrorMessage(deleteError, t("admin.deleteFailed")));
    } finally {
      setDeleting(false);
    }
  };

  // Only truthy when the user has typed something that hasn't been debounced yet.
  const isSearching = searchTerm !== debouncedSearch;

  // Only block the whole page on the true initial mount.
  if (initialLoading) {
    return (
      <div className="admin-loading">
        <span className="auth-spinner auth-spinner-brand"></span>
      </div>
    );
  }

  if (error) {
    return <ConnectionError onRetry={() => loadServices(page)} />;
  }

  return (
    <div className="admin-consultation-services-page">
      {/* Page Header */}
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">{t("admin.consultationServices")}</h1>
          <p className="admin-page-subtitle">{t("admin.consultationServicesDesc")}</p>
        </div>
        <button type="button" className="cs-add-btn" onClick={openCreateModal}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t("admin.addConsultationService")}
        </button>
      </div>

      {/* ─── Search Toolbar ─── */}
      <div className="cs-toolbar-wrap">
        <div className="cs-toolbar">
          <div className="cs-search-field">
            <span className="cs-search-field__icon">
              {isSearching ? (
                <span className="cs-search-spinner" />
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  width="17"
                  height="17"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              )}
            </span>
            <input
              type="text"
              className="cs-search-field__input"
              placeholder={t("admin.searchConsultationServices", "Search consultation services...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
            />
            {searchTerm && (
              <button
                className="cs-search-field__clear"
                onClick={handleClearSearch}
                aria-label="Clear search"
                type="button"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="13"
                  height="13"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        </div>
        {/* Thin progress bar — visible only during silent re-fetches */}
        <div
          className={`cs-fetch-bar ${isFetching ? "cs-fetch-bar--active" : ""}`}
        />
      </div>

      {/* Consultation Service Cards */}
      {isFetching && services.length === 0 ? (
        <div className="admin-loading">
          <span className="auth-spinner auth-spinner-brand"></span>
        </div>
      ) : services.length === 0 ? (
        <EmptyState
          icon={
            <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M15 8v8H5V8h10m1-2H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11l-4 4V7a1 1 0 0 0-1-1z" />
            </svg>
          }
          title={debouncedSearch ? t("admin.noSearchResults", "No results found") : t("admin.noConsultationServices")}
          description={debouncedSearch ? t("admin.noSearchResultsDesc", "Try a different search term.") : t("admin.noConsultationServicesDesc")}
        />
      ) : (
        <div className="cs-list">
          {services.map((service) => (
            <article key={service.id} className="cs-card">
              <div className="cs-card__thumb">
                {service.imageUrl ? (
                  <img src={service.imageUrl} alt={service.titleEn} />
                ) : (
                  <div className="cs-card__thumb-placeholder">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                  </div>
                )}
              </div>

              <div className="cs-card__info">
                <div className="cs-card__title-row">
                  <span className="cs-card__title">{service.titleEn}</span>
                  <span className="cs-card__title-ar">{service.titleAr}</span>
                </div>
                <div className="cs-card__meta">
                  {service.durationMinutes !== undefined && service.durationMinutes !== null && (
                    <span className="cs-meta-chip cs-meta-chip--duration">
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      {t("booking.durationValue", { minutes: service.durationMinutes })}
                    </span>
                  )}
                  <span className="cs-meta-chip">#{service.sortOrder}</span>
                </div>
              </div>

              <div className="cs-card__actions">
                <button
                  type="button"
                  className="cs-action-btn cs-action-btn--edit"
                  onClick={() => openEditModal(service)}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  {t("admin.edit")}
                </button>
                <button
                  type="button"
                  className="cs-action-btn cs-action-btn--delete"
                  onClick={() => handleDeleteClick(service)}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  {t("admin.delete")}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Numbered pagination */}
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={loadServices}
        disabled={isFetching}
      />

      {/* Create / Edit Modal */}
      <Modal
        isOpen={formModalOpen}
        onClose={closeFormModal}
        title={editingService ? t("admin.editConsultationService") : t("admin.addConsultationService")}
        className="cs-form-modal"
      >
        <form className="cs-form" onSubmit={handleSubmit}>
          <div className="cs-form__row">
            <div className="cs-form__field">
              <label className="cs-form__label">{t("admin.titleEn")}</label>
              <input
                type="text"
                className="cs-form__input"
                value={form.titleEn}
                onChange={handleFieldChange("titleEn")}
                required
                maxLength={160}
              />
            </div>
            <div className="cs-form__field">
              <label className="cs-form__label">{t("admin.titleAr")}</label>
              <input
                type="text"
                className="cs-form__input"
                dir="rtl"
                value={form.titleAr}
                onChange={handleFieldChange("titleAr")}
                required
                maxLength={160}
              />
            </div>
          </div>

          <div className="cs-form__row">
            <div className="cs-form__field">
              <label className="cs-form__label">{t("admin.summaryEn")}</label>
              <textarea
                className="cs-form__input cs-form__textarea"
                value={form.summaryEn}
                onChange={handleFieldChange("summaryEn")}
                maxLength={2000}
                rows={2}
              />
            </div>
            <div className="cs-form__field">
              <label className="cs-form__label">{t("admin.summaryAr")}</label>
              <textarea
                className="cs-form__input cs-form__textarea"
                dir="rtl"
                value={form.summaryAr}
                onChange={handleFieldChange("summaryAr")}
                maxLength={2000}
                rows={2}
              />
            </div>
          </div>

          <div className="cs-form__row">
            <div className="cs-form__field">
              <label className="cs-form__label">{t("admin.durationMinutes")}</label>
              <input
                type="number"
                className="cs-form__input"
                min="1"
                value={form.durationMinutes}
                onChange={handleFieldChange("durationMinutes")}
              />
            </div>
            <div className="cs-form__field">
              <label className="cs-form__label">{t("admin.sortOrder")}</label>
              <input
                type="number"
                className="cs-form__input"
                min="1"
                value={form.sortOrder}
                onChange={handleFieldChange("sortOrder")}
                required
              />
            </div>
          </div>

          <div className="cs-form__field">
            <label className="cs-form__label">{t("admin.image")}</label>
            <div className="cs-image-upload">
              {imagePreview && (
                <div className="cs-image-upload__preview">
                  <img src={imagePreview} alt={t("admin.currentImage")} />
                </div>
              )}
              <label className="cs-image-upload__picker">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                {t("admin.chooseImage")}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  hidden
                />
              </label>
            </div>
          </div>

          <div className="cs-form__actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={closeFormModal}
              disabled={saving}
            >
              {t("admin.cancel")}
            </button>
            <button type="submit" className="btn btn-filled" disabled={saving}>
              {saving ? (
                <span className="cs-btn-loading">
                  <span className="auth-spinner auth-spinner-white" />
                </span>
              ) : (
                t("admin.save")
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        title={t("admin.deleteConsultationServiceConfirmTitle")}
        message={t("admin.deleteConsultationServiceConfirmDesc")}
        confirmLabel={t("admin.delete") || "Delete"}
        cancelLabel={t("admin.cancel") || "Cancel"}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
        type="danger"
      />

      {/* Floating scroll-to-top */}
      <button
        className={`admin-go-to-top ${showScrollTop ? "visible" : ""}`}
        onClick={scrollToTop}
        aria-label="Go to top"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
    </div>
  );
}

export default ConsultationServicesPage;
